import { type ReactElement, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { IconButton } from "@similarweb/ui-components";

import {
    StyledAddConditionContainer,
    StyledEmptyText,
} from "app/pages/sales-intelligence/common-components/filters-panel";

import advancedFilteringI18nKeys from "../../../utils/i18n";

import { ConditionModal } from "./ConditionModal";
import type { GroupViewProps } from "./types";

const DEFAULT_VALUE = {};

/**
 * Filter-view half of the compound (per-condition) group filter (SI-8738): the empty-state description and the
 * centered "+ Add condition" trigger that opens a fresh {@link ConditionModal}. The condition cards themselves
 * live in {@link DynamicGroupFilterConditionsResultView} (the always-visible result view), mirroring how the
 * Technologies filter splits its add trigger from its result cards.
 */
export const DynamicGroupFilterConditionsView = ({
    filter,
    value = DEFAULT_VALUE,
    onFilterUpdate,
}: GroupViewProps): ReactElement => {
    const { translate } = useTranslate();

    const [isOpen, setIsOpen] = useState(false);

    const hasConditions = !filter.isValueEmpty(value);

    const applyDraft = (next?: typeof value): void => {
        onFilterUpdate(next);
        setIsOpen(false);
    };

    return (
        <div data-automation="dynamic-group-conditions">
            <StyledEmptyText isHidden={hasConditions}>
                {translate(advancedFilteringI18nKeys.groupDialog.emptyDescription)}
            </StyledEmptyText>

            <StyledAddConditionContainer>
                <IconButton
                    type="flat"
                    iconName="add"
                    onClick={() => setIsOpen(true)}
                    dataAutomation="dynamic-group-add-condition"
                >
                    {translate(advancedFilteringI18nKeys.groupDialog.addCondition)}
                </IconButton>
            </StyledAddConditionContainer>

            <ConditionModal
                isOpen={isOpen}
                filter={filter}
                value={value}
                onApply={applyDraft}
                onClose={() => setIsOpen(false)}
            />
        </div>
    );
};
